import { localizePath } from "@plaatplan/i18n";
import { useState } from "react";
import { isMobileAppPromoEnabled } from "../config/featureFlags";
import { getGuideHowTo } from "../content/guideHowTo";
import { LocalizedLink } from "../i18n/LocalizedLink";
import { SeoHead } from "../seo/SeoHead";
import { useGuideLinks } from "../i18n/useGuideLinks";
import { useInfoPages } from "../i18n/useInfoPages";
import { useLocale } from "../i18n/useLocale";
import { DismissibleMobileAppPromo } from "../promo/DismissibleMobileAppPromo";
import { InfoPageContent } from "../ui/InfoPageContent";
import { HowGuideVisual } from "../ui/guides/HowGuideVisual";
import { SimulatorGuideVisual } from "../ui/guides/SimulatorGuideVisual";
import { WerkplanGuideVisual } from "../ui/guides/WerkplanGuideVisual";
import { InfoPageModal } from "../ui/InfoPageModal";
import { SiteFooter } from "../ui/SiteFooter";
import { SiteHeader } from "../ui/SiteHeader";

const VISUAL_GUIDES = {
  how: HowGuideVisual,
  werkplanExport: WerkplanGuideVisual,
  cutSimulator: SimulatorGuideVisual,
};

export function GuidePage({ pageId }) {
  const { display, preferences } = useLocale();
  const guideLinks = useGuideLinks();
  const guide = guideLinks.find((item) => item.pageId === pageId);
  const infoPages = useInfoPages();
  const page = infoPages[pageId];
  const [infoPage, setInfoPage] = useState(null);
  const VisualGuide = VISUAL_GUIDES[pageId];
  const isVisual = Boolean(VisualGuide);
  const showAppPromo = isMobileAppPromoEnabled();

  if (!guide) return null;
  if (!page && !VisualGuide) return null;

  const title = guide.title;
  const heading = page?.title ?? guide.title.replace(/\s*\|\s*Plaatplan$/, "");
  const howTo = getGuideHowTo(infoPages, pageId, preferences.locale);
  const guidePath = localizePath(guide.path, preferences.locale);

  return (
    <div className={`page page--guide${isVisual ? " page--guide-visual" : ""}`}>
      <SeoHead
        title={title}
        description={guide.description}
        path={guidePath}
        breadcrumb={[
          { name: "Plaatplan", path: localizePath("/", preferences.locale) },
          { name: heading, path: guidePath },
        ]}
        howTo={howTo}
      />
      <a className="skip-link" href="#guide-main">
        {display.t("web.shell.skipLink")}
      </a>
      <SiteHeader />

      <main id="guide-main" className="guide-main" tabIndex={-1}>
        <article className={`guide-article${isVisual ? " guide-article--visual" : ""}`}>
          <header className="guide-header">
            <p className="info-page-kicker">{page?.kicker ?? display.t("web.guides.kicker")}</p>
            <h1>{heading}</h1>
          </header>

          <div className="guide-body">
            {VisualGuide ? (
              <VisualGuide />
            ) : (
              <InfoPageContent pageId={pageId} page={page} stepAnchors />
            )}
          </div>

          {showAppPromo ? (
            <DismissibleMobileAppPromo
              guidePageId={pageId}
              layout="tablet"
              variant="guide"
            />
          ) : null}

          <footer className="guide-footer">
            <LocalizedLink to="/" className="primary-button guide-cta">
              {display.t("web.guides.toPlanner")}
            </LocalizedLink>
          </footer>
        </article>
      </main>

      <SiteFooter onOpenPage={setInfoPage} />

      {infoPage && (
        <InfoPageModal
          pageId={infoPage}
          page={infoPages[infoPage]}
          onClose={() => setInfoPage(null)}
        />
      )}
    </div>
  );
}
